import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconClock } from '@sorare/core/src/atoms/icons/system/iconClock.ts';
import { iconInfoCircle } from '@sorare/core/src/atoms/icons/system/iconInfoCircle.ts';
import { Centered } from '@sorare/core/src/atoms/layout/Centered';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BrandL, BrandS, BrandXS } from '@sorare/core/src/atoms/typography';
import { useTimeLeft } from '@sorare/core/src/hooks/useTimeLeft';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { SpecialCraftHeader_PromotionalEventInterface } from './__generated__/index.graphql.ts';

const Header = styled.header`
  width: 100%;
  position: relative;
  z-index: 1;
  @media ${tabletAndAbove} {
    width: calc(40 * var(--vw));
    min-width: 350px;
    max-width: fit-content;
  }
`;
const Illustration = styled.img`
  max-width: 140px;
  height: 100%;
  max-height: calc(7 * var(--unit));
  object-fit: contain;
`;

const Background = styled(Vertical).attrs({ gap: 0, center: true })`
  position: relative;
  padding: var(--double-unit)
    clamp(var(--double-unit), calc(10 * var(--vw)), 100px);
  @media ${tabletAndAbove} {
    padding: var(--quadruple-unit) var(--quadruple-unit) var(--double-unit)
      var(--quadruple-unit);
  }
  gap: 0;
  text-align: center;

  background-color: var(--c-nl-300);

  z-index: 1;

  /*shape*/
  --bevel: var(--double-unit);
  clip-path: polygon(
    0 0,
    100% 0,
    100% calc(100% - var(--bevel)),
    calc(100% - var(--bevel)) 100%,
    var(--bevel) 100%,
    0 calc(100% - var(--bevel))
  );

  /* border */
  &::before {
    content: '';
    pointer-events: none;
    position: absolute;
    inset: 0;
    background: var(--card-pull-color);
    --border-size: 1px;

    clip-path: polygon(
      evenodd,
      0 0,
      100% 0,
      100% 100%,
      0 100%,
      0 0,
      var(--border-size) var(--border-size),
      calc(100% - var(--border-size)) var(--border-size),
      calc(100% - var(--border-size))
        calc(100% - var(--border-size) - var(--bevel)),
      calc(100% - var(--border-size) - var(--bevel))
        calc(100% - var(--border-size)),
      calc(var(--bevel) + var(--border-size)) calc(100% - var(--border-size)),
      var(--border-size) calc(100% - var(--border-size) - var(--bevel)),
      var(--border-size) var(--border-size)
    );
  }
  &:after {
    pointer-events: none;
    content: '';
    position: absolute;
    top: 75%;
    left: 0;
    right: 0;
    bottom: 0;
    .limited & {
      --rarity-color: var(--c-rgb-limited);
    }
    .rare & {
      --rarity-color: var(--c-rgb-rare);
    }
    .super_rare & {
      --rarity-color: var(--c-rgb-superRare);
    }
    .unique & {
      --rarity-color: var(--c-rgb-unique);
    }
    background:
      linear-gradient(
        to right,
        transparent,
        rgb(var(--rarity-color)),
        transparent
      ),
      radial-gradient(
        ellipse at bottom,
        rgba(var(--rarity-color), 0.25) 0%,
        transparent 75%
      );
    background-position:
      0 calc(100% - 1px),
      0 0;
    background-size:
      100% 1px,
      cover;
    background-repeat: no-repeat;
    mix-blend-mode: screen;
  }
  justify-content: flex-start;
`;

const TimeLeftBanner = styled(Horizontal)`
  position: relative;
  z-index: 1;
  width: max-content;
  margin: var(--half-unit) auto;
  gap: var(--half-unit);
  border-radius: var(--half-unit);

  color: var(--c-black);
  background-image: var(--c-gradient-gold);

  padding: var(--half-unit);

  @media ${tabletAndAbove} {
    margin: 0;
    border-radius: var(--half-unit) var(--half-unit) 0 var(--half-unit);
    position: absolute;
    z-index: 1;
    right: calc(-1 * var(--unit));
    top: var(--unit);
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      right: 0;
      width: 0;
      height: 0;
      border-right: var(--unit) solid transparent;
      border-top: var(--unit) solid var(--c-yellow-700);
    }
  }
`;

const TintedBrandS = styled(BrandS)`
  background-image: linear-gradient(
    to bottom,
    var(--c-white),
    var(--header-main-color)
  );
  @supports (color: hsl(from var(--header-main-color) h 255 l)) {
    background-image: linear-gradient(
      to bottom,
      var(--c-white),
      hsl(from var(--header-main-color) h 255 l)
    );
  }
  background-clip: text;
  color: transparent;
`;

type Props = {
  promo: SpecialCraftHeader_PromotionalEventInterface;
};

export const SpecialCraftHeader = ({ promo }: Props) => {
  const { message } = useTimeLeft(promo.endDate);

  return (
    <Header>
      <Background>
        <Vertical gap={1}>
          <Vertical gap={0.5}>
            <TintedBrandS>
              <FormattedMessage defaultMessage="Special" />
            </TintedBrandS>
            <Centered
              right={
                !!promo.blogUrl && (
                  <span
                    style={{ marginLeft: 'var(--unit)', alignSelf: 'start' }}
                  >
                    <LinkOverlay href={promo.blogUrl} target="_blank">
                      <FontAwesomeIcon icon={iconInfoCircle} size="xs" />
                    </LinkOverlay>
                  </span>
                )
              }
            >
              {promo.logoUrl ? (
                <Illustration src={promo.logoUrl} alt={promo.title} />
              ) : (
                <BrandL>{promo.title}</BrandL>
              )}
            </Centered>
            <Horizontal
              style={{ justifyContent: 'center', alignItems: 'flex-start' }}
            />
          </Vertical>
        </Vertical>
      </Background>
      <TimeLeftBanner>
        <FontAwesomeIcon icon={iconClock} size="xs" />
        <BrandXS>
          <FormattedMessage
            defaultMessage="Ends in {timeLeft}"
            values={{ timeLeft: message }}
          />
        </BrandXS>
      </TimeLeftBanner>
    </Header>
  );
};

SpecialCraftHeader.fragments = {
  PromotionalEventInterface: gql`
    fragment SpecialCraftHeader_PromotionalEventInterface on PromotionalEventInterface {
      slug
      title
      blogUrl
      endDate
      logoUrl
    }
  ` as TypedDocumentNode<SpecialCraftHeader_PromotionalEventInterface>,
};
